import { Box, Grid, Stack, Typography } from '@mui/material';

import { useEffect, useState, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { cloneDeep, isEmpty } from 'lodash';

import { getControlValidationTerms } from 'common/api/index';
import { ReactComponent as Regenerate } from 'common/assets/images/ai-generate.svg';
import { ReactComponent as CircleCrossIcon } from 'common/assets/images/circle-cross-icon.svg';
import { ReactComponent as CircleIcon } from 'common/assets/images/circle-icon.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import PromptSelect from 'common/components/PromptSelect/PromptSelect';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	AI_SIMULATION_GENAI_DISCLAIMER,
	AI_SIMULATION_MODEL_KEYS,
	LOCATION_OPTIONS,
} from 'common/constants/AISimulationConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	CONTROL_TAB_LABEL,
	ENTITY_TYPE,
	ICONTROL_TAB_LOADERS,
	WHY_LABEL,
} from 'common/constants/TaxonomyConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { setControlStepsData } from 'common/store/reducer/controlTestScriptReducer';

import {
	generateAISimulationResult,
	genControlStatement,
	genControlTestScript,
	generateAISimulationPromptResult,
	genPromptControlStatement,
	generateControlTestPromptResult,
} from 'features/AiSimulation/api/index';
import { fiveWValidationResponse } from 'features/RiskProfile/Taxonomy/RelationMapping/mappingUtils';
import ControlTestScriptStepsLayout from 'features/TestMonitoring/TestScriptHubLandingPage/ControlTestSteps/ControlTestScriptStepsLayout';

import {
	IEntityStatementSectionProps,
	IEntityStatementLoaders,
} from '../api/type-def';
import { getSummaryLabel } from '../TaxonomyUtils';

import { RootState } from '../../../../common/store/AppStore';

import entityStyles from './EntityLanding.module.scss';
import styles from './EntityStatementSection.module.scss';

const { PROCESS, RISK, CONTROL } = ENTITY_TYPE;
const { CONTROL_TEST_SCRIPTS, CONTROL_STATEMENT, CONTROL_OBJECTIVE } =
	CONTROL_TAB_LABEL;
const {
	CONTROL_OBJECTIVE_LOADER,
	CONTROL_STATEMENT_LOADER,
	CONTROL_TEST_SCRIPTS_LOADER,
} = ICONTROL_TAB_LOADERS;
const {
	CONTROL_OBJECTIVE_KEY,
	CONTROL_STATEMENT_KEY,
	RISK_STATEMENT_KEY,
	CONTROL_TEST_SCRIPTS_KEY,
} = AI_SIMULATION_MODEL_KEYS;

const EntityStatementSection = (props: IEntityStatementSectionProps) => {
	const {
		entityType,
		objective,
		tabValue,
		controlTestScripts,
		controlStatement,
		onTextSave,
		type = '',
		frequency = '',
		isFieldEditable = false,
		disableBtnStatus,
	} = props;
	const dispatch = useDispatch();
	const { OK } = HTTP_STATUS_CODES;
	const { DISCLAIMER } = AI_SIMULATION_GENAI_DISCLAIMER;
	const { PRC_INVENTORY: PRC } = LOCATION_OPTIONS;
	const originalTestScript = cloneDeep(controlTestScripts);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);
	const controlStepsList = useSelector(
		(state: RootState) => state.controlTestScript.controlStepsList,
	);

	const initialCtrlObjectiveText = cloneDeep(objective || '');
	const intialCtrlStatementText = cloneDeep(controlStatement || '');

	const [objectiveText, setObjective] = useState<string>(objective || '');
	const [statement, setStatement] = useState<string>(controlStatement || '');
	const [testscript, setTestScript] = useState<string>(
		controlTestScripts || '',
	);
	const [validationData, setValidationData] = useState<any>();
	const [isValidationDataLoading, setValidationDataLoading] =
		useState<boolean>(false);
	const [isGrammerCheckSelected, setGrammerCheckSelection] =
		useState<boolean>(false);
	const [parts, setParts] = useState<string[]>();
	const [isEditable, setIsEditable] = useState(false);
	const [testScriptSteps, setTestScriptSteps] = useState<string[]>([]);
	const [isRegenerateLoading, setRegenerateDataLoading] =
		useState<IEntityStatementLoaders>({
			objective: false,
			statement: false,
			testscripts: false,
		});

	const [initialStatement, setInitialStatement] = useState<string>(
		controlStatement || '',
	);
	const [initialObjectiveText, setInitialObjectiveText] = useState<string>(
		objective || '',
	); // Store initial value
	const [, setInitialTestScript] = useState<string>(controlTestScripts || ''); // Store initial value
	const [isSaved, setIsSaved] = useState(true);
	const [isTestScriptStepLoaded, setTestScriptLoading] = useState(true);
	const handleTextAreaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;
		resetAllTabText(newValue, tabValue);
		checkIfSaved(newValue, tabValue);
	};

	const checkIfSaved = (value: string, tabValue: string) => {
		const lowerTabValue = tabValue?.toLowerCase();
		const result = originalTestScript?.split(/\d{1,5}\.\s/).filter(Boolean);
		const initialTextMapping: Record<string, string> = {
			[CONTROL_OBJECTIVE.toLowerCase()]: initialObjectiveText,
			[CONTROL_STATEMENT.toLowerCase()]: initialStatement,
			[CONTROL_TEST_SCRIPTS.toLowerCase()]: convertArrToString(result || []),
		};

		const initialText =
			initialTextMapping[lowerTabValue] || initialObjectiveText;
		setIsSaved(value === initialText);
	};

	useEffect(() => {
		if (controlStepsList?.length > 0) {
			const updatedTestScript = convertArrToString(controlStepsList);
			setTestScript(updatedTestScript || '');
			checkIfSaved(updatedTestScript, CONTROL_TEST_SCRIPTS);
		}
	}, [controlStepsList]);
	const handleClose = useCallback(() => {
		// close the toast message

		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);
	const resetAllTabText = (text: string, tabValue: string) => {
		switch (tabValue?.toLowerCase()) {
			case CONTROL_OBJECTIVE?.toLowerCase():
				setObjective(text);
				break;
			case CONTROL_STATEMENT?.toLowerCase():
				setStatement(text);
				break;
			case CONTROL_TEST_SCRIPTS?.toLowerCase():
				setTestScript(text);
				break;
			default:
				setObjective(text);
		}
	};
	const onPromptRegenerateAction = async (event: any) => {
		const promptKey = event.key;
		onRegenerateAction(promptKey);
	};
	const onRegenerateActionClick = async () => {
		onRegenerateAction('');
	};
	const onRegenerateAction = async (promptKey: string) => {
		setIsSaved(false);
		if (entityType === RISK) {
			callRegenerateActionForRiskData(promptKey);
		} else {
			callRegenerateActionForControlData(promptKey);
		}
	};
	const getPromptKey = () => {
		if (entityType === RISK) {
			return RISK_STATEMENT_KEY;
		}
		switch (tabValue?.toLowerCase()) {
			case CONTROL_OBJECTIVE?.toLowerCase():
				return CONTROL_OBJECTIVE_KEY;
			case CONTROL_STATEMENT?.toLowerCase():
				return CONTROL_STATEMENT_KEY;
			case CONTROL_TEST_SCRIPTS?.toLowerCase():
				return CONTROL_TEST_SCRIPTS_KEY;
			default:
				return CONTROL_OBJECTIVE_KEY;
		}
	};
	const callRegenerateActionForRiskData = async (promptKey: string) => {
		const selectedTab = CONTROL_OBJECTIVE_LOADER;
		resetRegenerateLoader(selectedTab, true);
		const text = initialCtrlObjectiveText;
		const res = promptKey.length
			? await generateAISimulationPromptResult(promptKey, text)
			: await generateAISimulationResult(RISK_STATEMENT_KEY, text, PRC);
		if (res?.statusCode === OK) {
			const resp = res?.responseBody?.answer || '';
			setObjective(resp);
			setInitialObjectiveText(resp);
			resetRegenerateLoader(selectedTab, false);
		} else {
			setObjective(objective);
			resetRegenerateLoader(selectedTab, false);
		}
	};
	const callRegenerateActionForControlData = async (promptKey: string) => {
		if (tabValue?.toLowerCase() === CONTROL_OBJECTIVE?.toLowerCase()) {
			callObjectiveRegenerateAction(promptKey);
		} else if (tabValue?.toLowerCase() === CONTROL_STATEMENT?.toLowerCase()) {
			callStatementRegenerateAction(promptKey);
		} else {
			callTestScriptRegenerateAction(promptKey);
		}
	};

	const callObjectiveRegenerateAction = async (promptKey: string) => {
		const selectedTab = CONTROL_OBJECTIVE_LOADER;
		resetRegenerateLoader(selectedTab, true);
		const text = initialCtrlObjectiveText;
		const res = promptKey?.length
			? await generateAISimulationPromptResult(promptKey, text)
			: await generateAISimulationResult(CONTROL_OBJECTIVE_KEY, text, PRC);
		if (res?.statusCode === OK) {
			const resp = res?.responseBody?.answer || '';
			setObjective(resp);
			resetRegenerateLoader(selectedTab, false);
		} else {
			setObjective(objective);
			resetRegenerateLoader(selectedTab, false);
		}
	};
	const callStatementRegenerateAction = async (key: string) => {
		const selectedTab = CONTROL_STATEMENT_LOADER;
		resetRegenerateLoader(selectedTab, true);
		setValidationData(undefined);
		setGrammerCheckSelection(false);
		const text = intialCtrlStatementText;
		const res = key.length
			? await genPromptControlStatement(text, frequency, type, '', key, PRC)
			: await genControlStatement(text, frequency, type, '', PRC);

		if (res?.statusCode === OK) {
			const resp = res?.responseBody?.answer || '';
			setStatement(resp);
			setInitialStatement(resp);
			resetRegenerateLoader(selectedTab, false);
		} else {
			handleApiError(res);
			setStatement(controlStatement || '');
			resetRegenerateLoader(selectedTab, false);
		}
	};
	const callTestScriptRegenerateAction = async (key: string) => {
		const selectedTab = CONTROL_TEST_SCRIPTS_LOADER;
		resetRegenerateLoader(selectedTab, true);
		const res = key.length
			? await generateControlTestPromptResult(key, statement, PRC)
			: await genControlTestScript(CONTROL_TEST_SCRIPTS_KEY, statement, PRC);
		if (res?.statusCode === OK) {
			const resp = res?.responseBody?.answer || '';
			setTestScript(resp);
			setInitialTestScript(resp);
			resetRegenerateLoader(selectedTab, false);
		} else {
			handleApiError(res);
			resetRegenerateLoader(selectedTab, false);
			setTestScript(controlTestScripts || '');
		}
	};

	const resetRegenerateLoader = (type: string, value: boolean) => {
		setRegenerateDataLoading((previous: any) => ({
			...previous,
			[type]: value,
		}));
	};
	const onGrammerCheckAction = async () => {
		setIsEditable(false);
		setGrammerCheckSelection(true);
		setValidationData(undefined);
		setParts([]);
		const payloadText = statement?.replace(/\s+/g, ' ');
		setValidationDataLoading(true);
		const response: any = await getControlValidationTerms(payloadText);
		if (response?.statusCode === OK) {
			setValidationData(response?.responseBody);
			setStatement(response?.responseBody?.controlDescription);
			setValidationDataLoading(false);
		} else {
			setValidationData(undefined);
			setValidationDataLoading(false);
			setGrammerCheckSelection(false);
			setStatement(controlStatement || '');
			handleApiError(response);
		}
	};

	const handleApiError = (data: any) => {
		dispatch(
			setToastMessage({
				message: data?.errorBody?.errorMessage || APP_GENERAL_ERROR_MSG,
				severity: 'error',
				isToastOpen: true,
			}),
		);
	};
	const editFieldHandler = () => {
		setIsEditable(true);
	};
	const onSaveHandler = () => {
		setIsSaved(true);
		const updatedText =
			{
				[CONTROL_TEST_SCRIPTS.toLowerCase()]:
					convertArrToString(controlStepsList),
				[CONTROL_STATEMENT.toLowerCase()]: statement,
			}[tabValue?.toLowerCase()] || objectiveText;
		const key =
			{
				[CONTROL_TEST_SCRIPTS.toLowerCase()]: 'controlTestScripts',
				[CONTROL_STATEMENT.toLowerCase()]: 'controlStatement',
			}[tabValue?.toLowerCase()] || 'objective';

		onTextSave(updatedText, key);
	};

	const convertArrToString = (data: string[]) => {
		const target = isEmpty(data) ? testScriptSteps : data;
		const arrWithoutNewLine = (target as Array<any>).map((item, index) => {
			if (index !== target.length - 1) {
				return `${index + 1}. ${item.substring(0, item.length - 1)}`;
			}
			return `${index + 1}. ${item}`;
		});
		return arrWithoutNewLine.join('\n');
	};
	useEffect(() => {
		if (tabValue) {
			setValidationData(undefined);
			setValidationDataLoading(false);
			setGrammerCheckSelection(false);
		}
	}, [tabValue]);
	useEffect(() => {
		const generateParts = validationData?.controlDescription?.split(
			new RegExp(
				`(${availableControlValidation
					?.map(({ word }) =>
						word?.toLowerCase()?.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&'),
					)
					?.join('|')})`,
				'gi',
			),
		);

		setParts(generateParts);
	}, [validationData]);
	const { fiveWValidation, availableControlValidation } =
		fiveWValidationResponse(validationData);
	/**
	 * @description method to get entity details
	 */
	const getDetailsInfo = () =>
		!(
			entityType?.toLowerCase() !== RISK.toLowerCase() || CONTROL.toLowerCase()
		);

	const getFiveWIcon = (data: any) =>
		data?.name === WHY_LABEL ? (
			<CircleCrossIcon fill={data?.color} />
		) : (
			<CircleIcon fill={data?.color} />
		);
	const getRegenerateLoading = () => {
		if (tabValue?.toLowerCase() === CONTROL_STATEMENT?.toLowerCase()) {
			return isRegenerateLoading?.statement;
		} else if (
			tabValue?.toLowerCase() === CONTROL_TEST_SCRIPTS?.toLowerCase()
		) {
			return isRegenerateLoading?.testscripts;
		}
		return isRegenerateLoading?.objective;
	};

	const getTextValue = () =>
		tabValue?.toLowerCase() === CONTROL_STATEMENT.toLowerCase()
			? statement
			: objectiveText;
	const getTextFieldEditatbleStatus = () => {
		let isTextFieldEditable = false;
		if (entityType !== RISK) {
			isTextFieldEditable =
				isFieldEditable &&
				((tabValue === CONTROL_OBJECTIVE && !disableBtnStatus?.objective) ||
					(tabValue === CONTROL_STATEMENT && !disableBtnStatus?.statement) ||
					(tabValue === CONTROL_TEST_SCRIPTS &&
						!disableBtnStatus?.testscripts));
		} else {
			isTextFieldEditable = isFieldEditable && !disableBtnStatus?.objective;
		}

		return isTextFieldEditable;
	};
	const getTextField = (val: string) => {
		const isTextFieldEditable = getTextFieldEditatbleStatus();

		return isTextFieldEditable ? (
			<TextFieldWithLabel
				inputProps={{
					'data-testid': `gen-textarea-${entityType}`,
				}}
				id={`${entityType}-textarea`}
				value={val}
				onChange={handleTextAreaChange}
				multiLine
				showLabel={false}
				error={false}
				helperText=""
				className={entityStyles['entity-statement-textfield-width']}
			/>
		) : (
			<Typography
				className={styles['entity-statement-summary-objective']}
				pt={1}
			>
				{val || ''}
			</Typography>
		);
	};

	const getTestScriptSaveBtnStatus = () => {
		let isSaveEnabled = false;
		let updatedTestScript = '';
		let initialTestScriptText = '';

		if (testScriptSteps?.length > 0 || controlStepsList?.length > 0) {
			updatedTestScript = convertArrToString(controlStepsList);
			initialTestScriptText =
				originalTestScript
					?.split(/\d{1,5}\.\s/)
					.filter(Boolean)
					.join(' ') || '';
		}

		if (testscript?.length === 0 || updatedTestScript?.length === 0) {
			isSaveEnabled = true;
		} else {
			isSaveEnabled =
				initialTestScriptText === updatedTestScript ||
				disableBtnStatus?.testscripts ||
				isRegenerateLoading?.testscripts;
		}

		return isSaveEnabled;
	};

	const getStatementSaveBtnStatus = () => {
		if (isGrammerCheckSelected) {
			return false;
		}
		return (
			statement?.trim() === controlStatement?.trim() ||
			disableBtnStatus?.statement ||
			isRegenerateLoading?.statement
		);
	};

	const getGrammerCheckSection = () =>
		isValidationDataLoading ? (
			<Typography
				className={styles['entity-statement-summary-objective-loader']}
				pt={1}
			/>
		) : (
			<>
				<Grid container className={styles['entity-statement-fivew-grid']}>
					<Grid item pt={1}>
						<Stack
							direction="row"
							className={styles['entity-statement-fivew-mainstack']}
						>
							{fiveWValidation?.map((res) => (
								<Stack
									key={res.id}
									direction="row"
									className={styles['entity-statement-fivew-stack']}
								>
									<Box className={styles['validation-block']}>
										{getFiveWIcon(res)}
									</Box>
									<Typography className={styles['entity-statement-what-text']}>
										{res?.name}
									</Typography>
								</Stack>
							))}
						</Stack>
					</Grid>
					<Grid item className={styles['entity-statement-fivew-grid-check']}>
						<Typography className={styles['entity-statement-grammar-score']}>
							<span className={styles['entity-statement-grammar-score-value']}>
								{`Grammar score: ${validationData?.grammarScore}`}
							</span>
						</Typography>
					</Grid>
				</Grid>
				<Stack>
					{isEditable ? (
						<TextFieldWithLabel
							inputProps={{
								'data-testid': 'gen-textarea-output',
							}}
							id="validation-textarea"
							value={statement}
							onChange={handleTextAreaChange}
							multiLine
							showLabel={false}
							error={false}
							helperText=""
							className={entityStyles['entity-statement-textfield-width']}
						/>
					) : (
						<div
							onClick={editFieldHandler}
							className={styles['entity-statement-fivew-textfield']}
							aria-hidden="true"
							data-testid="output-field"
						>
							<Typography className={styles['output-field-style']}>
								{parts?.map((part: any) => {
									const matchingPair = availableControlValidation?.find(
										({ word }) => word?.toLowerCase() === part?.toLowerCase(),
									);
									const color = matchingPair ? matchingPair.color : undefined;
									const textDecoration = matchingPair ? 'underline' : undefined;

									return (
										<span
											key={part}
											style={{
												backgroundColor: color,
												textDecoration,
												textDecorationColor: color,
												overflow: 'auto',
											}}
										>
											{part}
										</span>
									);
								})}
							</Typography>
						</div>
					)}
				</Stack>
			</>
		);
	const getSaveBtnStatus = useCallback(() => {
		if (isValidationDataLoading) {
			return true;
		}
		let btnStatus = false;
		if (entityType === RISK) {
			btnStatus =
				objective?.trim() === objectiveText?.trim() ||
				disableBtnStatus?.objective ||
				isRegenerateLoading?.objective;
		} else {
			if (tabValue === CONTROL_OBJECTIVE) {
				btnStatus =
					objective?.trim() === objectiveText?.trim() ||
					disableBtnStatus?.objective ||
					isRegenerateLoading?.objective;
			}
			if (tabValue === CONTROL_STATEMENT) {
				btnStatus = getStatementSaveBtnStatus();
			}

			if (tabValue === CONTROL_TEST_SCRIPTS) {
				btnStatus = getTestScriptSaveBtnStatus();
			}
		}

		return btnStatus;
	}, [
		isValidationDataLoading,
		tabValue,
		objective,
		objectiveText,
		statement,
		controlStatement,
		testscript,
		controlTestScripts,
		getRegenerateLoading,
	]);

	const getRegenerateBtnStatus = () => {
		const isObjectiveRegenerateBtnEnabled =
			tabValue === CONTROL_OBJECTIVE && disableBtnStatus?.objective;

		const isStatementRegenerateBtnEnabled =
			tabValue === CONTROL_STATEMENT && disableBtnStatus?.statement;
		const isTestScriptRegenerateBtnEnabled =
			tabValue === CONTROL_TEST_SCRIPTS && disableBtnStatus?.testscripts;

		return (
			isObjectiveRegenerateBtnEnabled ||
			isStatementRegenerateBtnEnabled ||
			isTestScriptRegenerateBtnEnabled ||
			getRegenerateLoading()
		);
	};

	useEffect(() => {
		if (tabValue === CONTROL_TEST_SCRIPTS && testscript?.length) {
			const result = testscript.split(/\d{1,5}\.\s/).filter(Boolean);
			setTestScriptSteps(result);
			setControlStepsData([...result]);
		}
	}, [tabValue, testscript.length]);

	useEffect(() => {
		setTestScriptLoading(false);
	}, [testScriptSteps]);
	return (
		<div className={entityStyles['entity-statement-container']}>
			{entityType?.toLowerCase() !== PROCESS.toLowerCase() && (
				<div className={entityStyles['detail-summary-bg']}>
					<Stack
						direction={getDetailsInfo() ? 'column' : 'row'}
						className={entityStyles['entity-summary-stack']}
					>
						<div
							className={
								!getDetailsInfo()
									? styles['summary-full-container']
									: entityStyles['summary-container']
							}
						>
							<Stack direction="column">
								{tabValue?.toLowerCase() ===
								CONTROL_TEST_SCRIPTS.toLowerCase() ? (
									<>
										<Typography
											className={styles['entity-statement-title']}
											data-testid={`${entityType}-statement`}
										>
											{getSummaryLabel(entityType, 'Control statement')}
										</Typography>
										<Typography
											className={styles['entity-statement-summary-objective']}
											pt={1}
										>
											{statement || ''}
										</Typography>
										<Typography
											mt={1}
											pb={1}
											className={styles['entity-statement-title']}
											data-testid={`${entityType}-testscript`}
										>
											{getSummaryLabel(entityType, tabValue)}
										</Typography>
										<ControlTestScriptStepsLayout
											type=""
											isActionsEnabled
											controValue=""
											generatedSteps={testScriptSteps}
											loading={isTestScriptStepLoaded}
											deleteModal
											hideActions={!getTextFieldEditatbleStatus()}
										/>
									</>
								) : (
									<>
										<Typography
											className={styles['entity-statement-title']}
											data-testid={`${entityType}-statement`}
											pb={1}
											pt={1}
										>
											{getSummaryLabel(entityType, tabValue)}
										</Typography>

										{!isGrammerCheckSelected
											? getTextField(getTextValue())
											: getGrammerCheckSection()}
									</>
								)}
							</Stack>
						</div>
					</Stack>
					{isFieldEditable && (
						<Stack className={styles['entity-statement-button-container']}>
							<Typography
								className={styles['entity-statement-button-disclaimer-txt']}
							>
								{DISCLAIMER}
							</Typography>
							<Stack
								direction="row"
								className={styles['entity-statement-buttons']}
							>
								<PromptSelect
									aiModelKey={getPromptKey()}
									btnType="secondary"
									iconClass={styles['regenerate-button-prompt']}
									groupClass={styles['entity-statement-regenerate-button']}
									compClass={styles['entity-statement-regenerate-button']}
									startIcon={
										!getRegenerateLoading() && (
											<Box
												className={
													styles['entity-statement-regenerate-button-icon']
												}
											>
												<Regenerate />
											</Box>
										)
									}
									data-testid="regenerate-button"
									disabled={getRegenerateBtnStatus() || isValidationDataLoading}
									clickHandler={onRegenerateActionClick}
									onSelectPrompt={onPromptRegenerateAction}
									loading={getRegenerateLoading()}
								>
									Regenerate
								</PromptSelect>
								{tabValue?.toLowerCase() ===
									CONTROL_STATEMENT.toLowerCase() && (
									<ButtonComp
										btnType="primary"
										compClass={styles['entity-statement-grammercheck-button']}
										data-testid="grammar-check-button"
										disabled={
											isValidationDataLoading || getRegenerateBtnStatus()
										}
										onClick={onGrammerCheckAction}
										loading={isValidationDataLoading}
									>
										Quality & grammar check
									</ButtonComp>
								)}
								<ButtonComp
									btnType="primary"
									compClass={styles['entity-statement-grammercheck-button']}
									data-testid="save-button"
									disabled={getSaveBtnStatus() || isSaved}
									onClick={onSaveHandler}
								>
									Save
								</ButtonComp>
							</Stack>
						</Stack>
					)}
				</div>
			)}
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleClose}
				vertical="top"
				horizontal="right"
			/>
		</div>
	);
};

export default EntityStatementSection;
